import { useLinkingURL } from 'expo-linking';
import { type Href, router } from 'expo-router';
import { useEffect, useRef } from 'react';
import useAuthStore from '@/stores/useAuthStore';
import useJoinIntentStore from '@/stores/useJoinIntentStore';
import useRootStore from '@/stores/useRootStore';
import getAuth, {
  isSignInWithEmailLink,
  signInWithEmailLink,
} from '@/utils/auth';

export default function useEmailLinkSignIn() {
  const lastUrlRef = useRef<string | null>(null);
  const linkingUrl = useLinkingURL();
  const hasHydratedJoinIntent = useJoinIntentStore(
    (state) => state.hasHydratedJoinIntent,
  );

  useEffect(() => {
    (async () => {
      const status = useAuthStore.getState().status;

      if (
        !hasHydratedJoinIntent ||
        !linkingUrl ||
        lastUrlRef.current === linkingUrl ||
        status === 'in'
      ) {
        return;
      }

      lastUrlRef.current = linkingUrl;

      const auth = getAuth();

      if (!(await isSignInWithEmailLink(auth, linkingUrl))) {
        return;
      }

      const storedEmail = useRootStore.getState().magicLinkEmail;

      if (!storedEmail) {
        useRootStore.setState({ pendingEmailLink: linkingUrl });
        router.navigate(getEmailSignInRoute());
        return;
      }

      try {
        await signInWithEmailLink(auth, storedEmail, linkingUrl);
        useRootStore.setState({
          magicLinkEmail: null,
          pendingEmailLink: null,
        });
      } catch {
        useRootStore.setState({ pendingEmailLink: linkingUrl });
        router.navigate(getEmailSignInRoute());
      }
    })();
  }, [hasHydratedJoinIntent, linkingUrl]);
}

function getEmailSignInRoute(): Href {
  const joinIntent = useJoinIntentStore.getState().joinIntent;

  if (joinIntent) {
    return {
      pathname: '/sign-in/email',
      params: { invite: 'true' },
    };
  }

  return '/sign-in/email';
}
